import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useState } from 'react';
import { GraphQLErrorCode } from '@insio/validation';
import { Button, ConfirmationModal, Paper } from '@insio/ui-toolkit';
import { RequestAccountDeletionDocument } from '@/graphql';
import { useSignOut } from '@/features/auth';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useMasterAccountBoardErrorMessage } from '@/hooks/use-master-account-board-error';

/** Whether a master-account-side admin was refused deleting a client that only its own admin may delete. */
function needsDirectAdmin(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      e => e.extensions?.['code'] === GraphQLErrorCode.CLIENT_DELETION_NEEDS_DIRECT_ADMIN
    )
  );
}

/**
 * Self-service account deletion (admin only). Confirming schedules the account for
 * permanent deletion after a grace period and logs everyone out; the account can be
 * restored by logging back in during the grace period.
 */
export function DeleteAccountSection() {
  const t = useFeatureTranslation('systemSettings.organization');
  const { signOut } = useSignOut();
  const { showError } = useToastHelpers();
  const masterAccountBoardError = useMasterAccountBoardErrorMessage();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // no-cache: writing PENDING_DELETION into the normalized cache would flash the
  // reactivation gate for a moment before the sign-out completes.
  const [requestDeletion, { loading }] = useMutation(RequestAccountDeletionDocument, {
    fetchPolicy: 'no-cache'
  });

  const handleConfirm = async () => {
    try {
      const { error } = await requestDeletion();
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (error) throw error;
    } catch (e) {
      showError(
        needsDirectAdmin(e)
          ? t('deleteAccount.needsDirectAdmin')
          : (masterAccountBoardError(e) ?? t('deleteAccount.requestError'))
      );
      setConfirmOpen(false);
      return;
    }
    // Sign out through the regular flow: destroys the current session server-side
    // (the bulk revocation can't kill the session carrying this very request) and
    // clears local auth state even when the mutation comes back unauthenticated.
    signOut();
  };

  return (
    <Paper
      showDivider={false}
      padding='none'
      className='border border-error-border'
      testId='delete-account-section'
    >
      <Paper.Content className='p-md'>
        <div className='flex flex-wrap items-center justify-between gap-md'>
          <div className='space-y-2xs'>
            <p className='font-medium text-foreground-base'>{t('deleteAccount.title')}</p>
            <p className='text-sm text-foreground-muted'>{t('deleteAccount.hint')}</p>
          </div>
          <Button outline='error' onClick={() => setConfirmOpen(true)}>
            {t('deleteAccount.button')}
          </Button>
        </div>
      </Paper.Content>

      <ConfirmationModal
        open={confirmOpen}
        variant='danger'
        title={t('deleteAccount.confirmTitle')}
        message={t('deleteAccount.confirmMessage')}
        warning={t('deleteAccount.confirmWarning')}
        confirmLabel={t('deleteAccount.confirmButton')}
        cancelLabel={t('deleteAccount.cancelButton')}
        loading={loading}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </Paper>
  );
}
